import { Fragment, useState } from 'react';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import initials from 'initials';
import { Translate, translate } from 'react-i18nify';
import { Link } from 'react-router-dom';
import ContentLoader from '../ContentLoader';
import CanPerform from '../CanPerform';
import {
  getGenderFromPersonalNumber,
  getAgeFromPersonalNumber,
  formatPersonalNumber,
  getEnvironmentPrefix,
  decamelize,
  classnames
} from '../../utils';
import { APPLICATION_BASE_PATH, CURRENT_ENV } from '../../constants';
import Button from '../Button';
import DropdownMenu from '../DropdownMenu';
import ProfileImageUploadModal from '../ProfileImageUploadModal';
import Generation from '../Generation';
import ShippingAddressEditorModal from '../ShippingAddressEditorModal';

/**
 * Partners that have a dedicated logo CSS class in MemberDetailsView.scss. Partner values coming
 * back from the backend that aren't in this list fall back to "<Name>-patient" text rendering.
 * Keep in sync with the `.profile-partner-badge--*` rules in the stylesheet.
 */
const PARTNERS_WITH_LOGO = ['euroaccident', 'apotekhjartat', 'mindoktor'];

const PartnerBadge = ({ partnerName }) => {
  const partnerKey = partnerName.toLowerCase();
  const hasLogo = PARTNERS_WITH_LOGO.includes(partnerKey);
  const label = `${partnerName}-patient`;
  return (
    <div
      className={classnames([
        'profile-partner-badge',
        `profile-partner-badge--${partnerKey}`,
        hasLogo ? 'profile-partner-badge--logo-only' : null
      ])}
      aria-label={label}
      title={label}
    >
      {hasLogo ? '' : label}
    </div>
  );
};

const ProfileCard = ({
  member,
  error,
  isLoading,
  userType,
  memberActions,
  actionsAuthority,
  onSendMessage,
  userAuthorities,
  getProfileImage,
  refetch,
  showingProtectedData,
  sourceInfo
}) => {
  const [fileUploadModalVisible, setFileUploadModalVisible] = useState(false);
  const [shippingAddressModalVisible, setShippingAddressModalVisible] = useState(false);
  const userInitials = initials(`${member.givenName} ${member.familyName}`);
  const birthday = member && member.personalNumber && moment(member.personalNumber.substr(0, 8));
  const hasProfileImage = !!member.profileImageUrl;
  const hasShippingAddress = member.shippingStreetaddress && member.shippingPostalCode && member.shippingCity;

  return (
    <>
      <div className="card-container user-profile pt-10">
        <CanPerform action={actionsAuthority}>
          {userType === 'member' ? (
            <div className="profile-card-dropdown">
              <DropdownMenu actions={memberActions} userAuthorities={userAuthorities} />
            </div>
          ) : null}
          {userType === 'member' ? (
            <div className="text-right">
              <span className="inline-flex center-align">
                <Tooltip
                  title={
                    <span className="fs-12">
                      <Translate value="member_details.clinic_link_tooltip" />
                    </span>
                  }
                  arrow
                  placement="top"
                >
                  <span className="icon question-mark mr-5"></span>
                </Tooltip>
                <a
                  href={`https://${
                    import.meta.env.VITE_API_ENV !== 'production'
                      ? getEnvironmentPrefix(import.meta.env.VITE_API_ENV || CURRENT_ENV) + '.'
                      : ''
                  }clinic.accumbo.se/app/${member.enableNextGeneration ? 'medicalrecord' : 'member'}/${member.guid}`}
                  className="clinic-link external"
                  target="clinic"
                >
                  Clinic
                </a>
              </span>
            </div>
          ) : null}
        </CanPerform>
        <ContentLoader isLoading={isLoading} error={error} errorTitleI18n="member_details.error_fetching">
          <div className="text-center mb-30">
            <div
              className={`pos-rel member-avatar-fallback x-large ${userType} ${
                hasProfileImage ? 'has-profile-image' : ''
              } ${getGenderFromPersonalNumber(member.personalNumber)} ${userInitials.length > 4 ? 'long-initial' : ''}`}
            >
              {!hasProfileImage ? (
                <span className="fallback">
                  {member && member.givenName
                    ? member.protectedIdentity && !showingProtectedData
                      ? '--'
                      : showingProtectedData && !member.familyName
                        ? '--'
                        : userInitials
                    : '...'}
                </span>
              ) : (
                <img src={member.profileImageUrl} alt="Avatar" />
              )}
              <CanPerform action={'manageUsers'}>
                {userType === 'user' ? (
                  <span
                    className="icon edit edit-profile-image pointer"
                    onClick={() => setFileUploadModalVisible(true)}
                  ></span>
                ) : null}
              </CanPerform>
            </div>
            <h2>{`${member.givenName}${member.familyName ? ` ${member.familyName}` : ''}`}</h2>
            {userType === 'member' ? (
              <h3>{formatPersonalNumber(member.personalNumber)}</h3>
            ) : (
              <h3 className="system-user-title">{member.title || translate('global.administrator')}</h3>
            )}
            {userType === 'member' && sourceInfo && (
              <div className="profile-source-row">
                {sourceInfo.acquisitionSource && sourceInfo.acquisitionSource !== 'Direct' ? (
                  <div className="profile-source-chip">{sourceInfo.acquisitionSource}</div>
                ) : null}
                {sourceInfo.partnerName ? <PartnerBadge partnerName={sourceInfo.partnerName} /> : null}
              </div>
            )}
            <div className="profile-badge-wrapper">
              {member.protectedIdentity && (
                <div className="profile-badge protected-identity">
                  <Translate value="member_details.protected_identity" />

                  <CanPerform action="manageProtectedIdentity">
                    <Button buttonType="text" onClick={() => refetch(!showingProtectedData)}>
                      {!showingProtectedData ? (
                        <Tooltip
                          title={
                            <span className="fs-12">
                              <Translate value="member_details.unmask_protected_identity_tooltip" />
                            </span>
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <Translate value="member_details.unmask_protected_identity" />
                          </span>
                        </Tooltip>
                      ) : (
                        <Translate value="member_details.mask_protected_identity" />
                      )}
                    </Button>
                  </CanPerform>
                </div>
              )}
              {member.deregisteredIdentity && (
                <div className="profile-badge deregistered">
                  <Translate
                    value={`member_details.deregistration_reason.${decamelize(member.deregisterIdentityReason)}`}
                  />
                </div>
              )}
              {userType === 'member' && (
                <Generation legacy>
                  <div className="profile-badge old-gen">OG</div>
                </Generation>
              )}
              {member.pendingDiagnosis &&
              member.pendingDiagnosis !== 'referred' &&
              member.pendingDiagnosis !== 'results' ? (
                <div className="profile-badge undiagnosed">
                  <Translate value="global.undiagnosed" />
                </div>
              ) : null}
            </div>
          </div>
          {userType === 'member' ? (
            <div className="mb-20">
              <Translate value={`global.${getGenderFromPersonalNumber(member.personalNumber)}`} />,{' '}
              {getAgeFromPersonalNumber(member.personalNumber)} <Translate value="global.years" />
              {birthday &&
              birthday.format('M') === moment().format('M') &&
              birthday.format('D') === moment().format('D') ? (
                <span role="img" aria-label="cake">
                  {' '}
                  🍰
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="mb-20">
            {userType === 'member' ? (
              <Fragment>
                <h4>
                  <Translate value="member_details.address_information" />
                </h4>
                {member.streetaddress && member.postalCode && member.city ? (
                  <>
                    <div>{member.streetaddress}</div>
                    <div>{`${member.postalCode} ${member.city}`}</div>
                  </>
                ) : (
                  <div className="data-missing">
                    <Translate value="member_details.no_address_information" />
                  </div>
                )}

                <div className="mt-20">
                  <h4>
                    <Translate value="member_details.shipping_address" />
                  </h4>

                  {hasShippingAddress ? (
                    <>
                      <div>{member.shippingStreetaddress}</div>
                      <div>{`${member.shippingPostalCode} ${member.shippingCity}`}</div>
                    </>
                  ) : (
                    <div className="data-missing">
                      <Translate value="member_details.no_shipping_address" />
                    </div>
                  )}
                </div>
                <div className="mt-10">
                  <Button buttonType="text" onClick={() => setShippingAddressModalVisible(true)}>
                    <Translate value={`member_details.${hasShippingAddress ? 'change' : 'set'}_shipping_address`} />
                  </Button>
                </div>
              </Fragment>
            ) : null}
          </div>
          <div className="mb-30">
            <h4>
              <Translate value="member_details.contact_information" />
            </h4>
            {!member.email && !member.phoneNumber ? (
              <div className="data-missing">
                <Translate value="member_details.no_contact_information" />
              </div>
            ) : null}
            {member.email ? <div className="break-word">{member.email}</div> : null}
            {member.phoneNumber ? <div>{member.phoneNumber}</div> : null}
          </div>
          <CanPerform action={userType === 'member' ? 'managePatients' : 'manageUsers'}>
            <Link
              className={classnames([
                'button',
                'button-secondary',
                'full-width',
                'inline-flex',
                member.protectedIdentity && !showingProtectedData && 'pointer-events-none opacity-6'
              ])}
              to={`/${APPLICATION_BASE_PATH}/${userType}/${member.guid}/edit`}
            >
              <Translate value="member_details.edit_profile" />
            </Link>
          </CanPerform>
          {userType === 'member' ? (
            <CanPerform action="sendCommunications">
              <Button onClick={onSendMessage} fullWidth={true}>
                <Translate value="member_details.send_message" />
              </Button>
            </CanPerform>
          ) : null}
        </ContentLoader>
      </div>
      <ProfileImageUploadModal
        isVisible={fileUploadModalVisible}
        onClose={() => setFileUploadModalVisible(false)}
        memberGuid={member.guid}
        memberName={`${member.givenName} ${member.familyName}`}
        hasProfileImage={hasProfileImage}
        getProfileImage={getProfileImage}
      />
      <ShippingAddressEditorModal
        isVisible={shippingAddressModalVisible}
        onClose={() => setShippingAddressModalVisible(false)}
      />
    </>
  );
};

export default ProfileCard;
